// src/services/firestoreService.ts
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  Timestamp,
  serverTimestamp,
  setDoc,
  deleteDoc,
  collectionGroup,
  limit,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth, db, storage } from "@/firebase"; // ✅ include auth so we always use the real Firebase UID
import {
  TaxCase,
  TaxCaseDoc,
  TaxDocument,
  UserProfile,
  UserDoc,
  UserRole,
  TaxMessage,
  FirmSettingsDoc,
  TaxTask,
  TaxStatus,
  FirmUpdate,
  SyncedStaffUser,
  FilersHubStaffRole,
} from "../types";

const functions = getFunctions(undefined as any, "us-central1");

const DEMO_FIRM_ID = "hannah-financial-demo";
const DEMO_LOGO =
  "https://storage.googleapis.com/msgsndr/ygqUk4JVwxGzD160zhvF/media/6969496b197f717a56ac3042.png";

/**
 * NOTE:
 * - We keep demo behavior intact
 * - Production writes use setDoc(..., {merge:true}) so missing docs won't crash updates
 */

/**
 * ✅ MULTI-TENANT FIX (core)
 * Your app sometimes receives a GHL locationId, but your Firestore firm doc id
 * might be different (or vice versa). If you write to the wrong "firmId", you
 * end up overwriting/shared branding across installs.
 *
 * This helper resolves ANY incoming firm key (doc id OR locationId) into the
 * canonical firms/{firmDocId} id and caches it for this session.
 */
const firmIdCache = new Map<string, string>();

const resolveFirmDocId = async (firmIdOrLocationId: string): Promise<string> => {
  if (!firmIdOrLocationId) return firmIdOrLocationId;
  if (firmIdOrLocationId === DEMO_FIRM_ID) return DEMO_FIRM_ID;

  const cached = firmIdCache.get(firmIdOrLocationId);
  if (cached) return cached;

  // 1) Direct firm doc id
  try {
    const directSnap = await getDoc(doc(db, "firms", firmIdOrLocationId));
    if (directSnap.exists()) {
      firmIdCache.set(firmIdOrLocationId, firmIdOrLocationId);
      return firmIdOrLocationId;
    }
  } catch {}

  // 2) Nested location.locationId
  try {
    const q1 = query(
      collection(db, "firms"),
      where("location.locationId", "==", firmIdOrLocationId),
      limit(1)
    );
    const snap1 = await getDocs(q1);
    if (!snap1.empty) {
      const canonical = snap1.docs[0].id;
      firmIdCache.set(firmIdOrLocationId, canonical);
      return canonical;
    }
  } catch {}

  // 3) Flat locationId
  try {
    const q2 = query(
      collection(db, "firms"),
      where("locationId", "==", firmIdOrLocationId),
      limit(1)
    );
    const snap2 = await getDocs(q2);
    if (!snap2.empty) {
      const canonical = snap2.docs[0].id;
      firmIdCache.set(firmIdOrLocationId, canonical);
      return canonical;
    }
  } catch {}

  // If nothing matches, fall back to the provided value (keeps behavior stable)
  firmIdCache.set(firmIdOrLocationId, firmIdOrLocationId);
  return firmIdOrLocationId;
};

export const getClientIp = async (): Promise<string> => {
  try {
    const res = await fetch("https://api.ipify.org?format=json");
    const data = await res.json();
    return data.ip || "Unknown";
  } catch {
    return "Unknown";
  }
};

export const getFirmBySlug = async (
  slug: string
): Promise<(FirmSettingsDoc & { id: string }) | null> => {
  if (slug === "hannah" || slug === "hannah-financial") {
    return {
      id: DEMO_FIRM_ID,
      name: "Hannah Financial",
      logoUrl: DEMO_LOGO,
      brandColor: "#a40000",
    };
  }

  try {
    const q = query(
      collection(db, "firms"),
      where("firmSlug", "==", slug.toLowerCase()),
      limit(1)
    );
    const snap = await getDocs(q);
    if (snap.empty) return null;

    const data = snap.docs[0].data();
    return { ...(data as any), id: snap.docs[0].id } as any;
  } catch {
    return null;
  }
};

/**
 * Resolve firm by:
 * 1) direct doc id
 * 2) firms where location.locationId == firmId
 * 3) firms where locationId == firmId
 */
export const getFirmById = async (
  firmId: string
): Promise<(FirmSettingsDoc & { id: string }) | null> => {
  if (firmId === DEMO_FIRM_ID) {
    return {
      id: DEMO_FIRM_ID,
      name: "Hannah Financial",
      logoUrl: DEMO_LOGO,
      brandColor: "#a40000",
    };
  }

  try {
    // 1) Direct doc id
    const docRef = doc(db, "firms", firmId);
    const snap = await getDoc(docRef);
    if (snap.exists()) return { ...(snap.data() as any), id: snap.id } as any;

    // 2) Nested location.locationId
    const q1 = query(
      collection(db, "firms"),
      where("location.locationId", "==", firmId),
      limit(1)
    );
    const snap1 = await getDocs(q1);
    if (!snap1.empty) {
      return { ...(snap1.docs[0].data() as any), id: snap1.docs[0].id } as any;
    }

    // 3) Flat locationId
    const q2 = query(
      collection(db, "firms"),
      where("locationId", "==", firmId),
      limit(1)
    );
    const snap2 = await getDocs(q2);
    if (!snap2.empty) {
      return { ...(snap2.docs[0].data() as any), id: snap2.docs[0].id } as any;
    }

    return null;
  } catch (err) {
    console.error(`[Vault] Firm resolution error for ${firmId}:`, err);
    return null;
  }
};

export const getFirmByCustomDomain = async (
  hostname: string
): Promise<(FirmSettingsDoc & { id: string }) | null> => {
  try {
    const q = query(
      collection(db, "firms"),
      where("customDomain", "==", hostname.toLowerCase()),
      limit(1)
    );
    const snap = await getDocs(q);
    if (snap.empty) return null;

    const data = snap.docs[0].data();
    return { ...(data as any), id: snap.docs[0].id } as any;
  } catch {
    return null;
  }
};

export const getFirmSettings = async (
  firmId: string = DEMO_FIRM_ID
): Promise<FirmSettingsDoc> => {
  const firm = await getFirmById(firmId);
  return firm || { name: "Hannah Financial", logoUrl: DEMO_LOGO, brandColor: "#a40000" };
};

export const getTaxCases = async (
  firmId: string,
  user: UserProfile
): Promise<TaxCase[]> => {
  if (firmId === DEMO_FIRM_ID) return MOCK_DEMO_CASES;

  const canonicalFirmId = await resolveFirmDocId(firmId);

  const qRef =
    user.role === UserRole.CLIENT
      ? query(collection(db, "firms", canonicalFirmId, "cases"), where("clientId", "==", user.uid))
      : query(collection(db, "firms", canonicalFirmId, "cases"), orderBy("lastUpdated", "desc"));

  const snapshot = await getDocs(qRef);
  return snapshot.docs.map((d) => ({ ...(d.data() as any), id: d.id } as any));
};

export const getTaxCaseById = async (
  firmId: string,
  caseId: string
): Promise<TaxCaseDoc | null> => {
  if (firmId === DEMO_FIRM_ID || caseId.includes("demo")) {
    return (MOCK_DEMO_CASES.find((c) => c.id === caseId) as any) || (MOCK_DEMO_CASES[0] as any);
  }

  const canonicalFirmId = await resolveFirmDocId(firmId);

  const snap = await getDoc(doc(db, "firms", canonicalFirmId, "cases", caseId));
  return snap.exists() ? ({ ...(snap.data() as any), id: snap.id } as TaxCaseDoc) : null;
};

export const getUserProfile = async (
  firmId: string,
  uid: string
): Promise<UserProfile | null> => {
  if (uid.includes("demo") || firmId === DEMO_FIRM_ID) {
    const rolePart = uid.split("_")[1] || "TaxPro";
    return {
      uid,
      displayName: `Hannah ${rolePart}`,
      email: `${rolePart.toLowerCase()}@hannahfinancial.com`,
      role: (UserRole as any)[rolePart] || UserRole.TaxPro,
      firmId: DEMO_FIRM_ID,
      createdAt: new Date().toISOString(),
    } as any;
  }

  const canonicalFirmId = await resolveFirmDocId(firmId);

  const snap = await getDoc(doc(db, "firms", canonicalFirmId, "users", uid));
  if (!snap.exists()) return null;

  const data = snap.data() as UserDoc;
  return {
    ...(data as any),
    role: data.role as UserRole,
    createdAt: (data.createdAt as any)?.toDate?.().toISOString?.() || new Date().toISOString(),
  } as any;
};

/**
 * ✅ Profile updates + avatar upload
 * FIXED:
 * - Always use the authenticated Firebase UID for storage path + writes.
 *   This prevents "ext_..." or CRM IDs from breaking Storage rules.
 * - Avatar stored at: firms/{firmId}/users/{uid}/avatar.{ext}
 * - avatarUrl written to:
 *   - firms/{firmId}/users/{uid}
 *   - users/{uid}
 */
export const updateUserProfile = async (
  firmId: string,
  uid: string,
  updates: Partial<UserDoc>,
  file?: File
) => {
  if (firmId === DEMO_FIRM_ID) return;

  const canonicalFirmId = await resolveFirmDocId(firmId);

  // ✅ Use real Firebase auth uid when available
  const authedUid = auth.currentUser?.uid || null;
  const resolvedUid = authedUid || uid;

  if (!resolvedUid) {
    throw new Error("No authenticated user found for profile update.");
  }

  let avatarUrl: string | undefined;

  if (file) {
    // Keep it stable: always overwrite same path so old avatars don't pile up
    const ext =
      file.type === "image/png"
        ? "png"
        : file.type === "image/webp"
        ? "webp"
        : "jpg";

    const storagePath = `firms/${canonicalFirmId}/users/${resolvedUid}/avatar.${ext}`;
    const sRef = ref(storage, storagePath);

    await uploadBytes(sRef, file, { contentType: file.type });
    avatarUrl = await getDownloadURL(sRef);
  }

  const payload: any = {
    ...updates,
    updatedAt: serverTimestamp(),
  };

  if (avatarUrl) payload.avatarUrl = avatarUrl;

  // Firm-scoped user doc
  await setDoc(doc(db, "firms", canonicalFirmId, "users", resolvedUid), payload, { merge: true });

  // Global user doc (AuthContext listens here too)
  await setDoc(doc(db, "users", resolvedUid), payload, { merge: true });
};

export const updateFirmSettings = async (firmId: string, updates: Partial<FirmSettingsDoc>) => {
  if (firmId === DEMO_FIRM_ID) return;

  const canonicalFirmId = await resolveFirmDocId(firmId);

  await setDoc(
    doc(db, "firms", canonicalFirmId),
    { ...(updates as any), updatedAt: serverTimestamp() },
    { merge: true }
  );
};

export const updateFirmLogo = async (firmId: string, file: File, type: string = "main") => {
  if (firmId === DEMO_FIRM_ID) return DEMO_LOGO;

  const canonicalFirmId = await resolveFirmDocId(firmId);

  // ✅ Firm-scoped asset path so logos cannot collide across firms
  const sRef = ref(storage, `firm_assets/${canonicalFirmId}/${type}_logo_${Date.now()}_${file.name}`);
  await uploadBytes(sRef, file, { contentType: file.type });
  const url = await getDownloadURL(sRef);

  await updateFirmSettings(canonicalFirmId, { logoUrl: url } as any);
  return url;
};

export const subscribeToDocuments = (
  firmId: string,
  caseId: string,
  callback: (docs: TaxDocument[]) => void
) => {
  if (firmId === DEMO_FIRM_ID) {
    callback(MOCK_DOCS.filter((d) => d.caseId === caseId || caseId === "all"));
    return () => {};
  }

  // NOTE: subscription cannot be async; firmId is expected to already be canonical
  return onSnapshot(
    query(collection(db, "firms", firmId, "cases", caseId, "documents"), orderBy("uploadedAt", "desc")),
    (snapshot) => {
      callback(
        snapshot.docs.map(
          (d) =>
            ({
              ...(d.data() as any),
              id: d.id,
              uploadedAt: (d.data() as any).uploadedAt?.toDate?.().toISOString() || new Date().toISOString(),
            } as TaxDocument)
        )
      );
    }
  );
};

/**
 * Global queue subscription (collectionGroup)
 * NOTE: Security rules must allow isStaff() reads on collectionGroup documents
 */
export const subscribeToAllFirmDocuments = (callback: (docs: TaxDocument[]) => void) => {
  const mockSession = sessionStorage.getItem("FILERSHUB_DEMO_SESSION");
  if (mockSession) {
    callback(MOCK_DOCS);
    return () => {};
  }

  return onSnapshot(
    query(collectionGroup(db, "documents"), orderBy("uploadedAt", "desc")),
    (snapshot) => {
      callback(
        snapshot.docs.map(
          (d) =>
            ({
              ...(d.data() as any),
              id: d.id,
              uploadedAt: (d.data() as any).uploadedAt?.toDate?.().toISOString?.() || new Date().toISOString(),
            } as TaxDocument)
        )
      );
    }
  );
};

export const uploadTaxDocument = async (
  file: File,
  firmId: string,
  caseId: string,
  category: string,
  userUid: string
): Promise<void> => {
  if (firmId === DEMO_FIRM_ID) return;

  const canonicalFirmId = await resolveFirmDocId(firmId);

  const caseDoc = await getTaxCaseById(canonicalFirmId, caseId);
  if (!caseDoc) return;

  const storagePath = `firm_uploads/${canonicalFirmId}/cases/${caseId}/${Date.now()}_${file.name}`;
  const storageRef = ref(storage, storagePath);

  await uploadBytes(storageRef, file, { contentType: file.type });

  const docRef = await addDoc(collection(db, "firms", canonicalFirmId, "cases", caseId, "documents"), {
    firmId: canonicalFirmId,
    caseId,
    fileName: file.name,
    storagePath,
    mimeType: file.type,
    sizeBytes: file.size,
    category,
    uploadedBy: userUid,
    uploadedAt: Timestamp.now() as any,
    status: "PENDING",
    clientId: (caseDoc as any).clientId,
    clientName: (caseDoc as any).clientName,
    year: (caseDoc as any).year,
    filingType: (caseDoc as any).filingType,
  });

  await updateDoc(docRef, { id: docRef.id });
};

export const subscribeToMessages = (
  firmId: string,
  caseId: string,
  callback: (msgs: TaxMessage[]) => void
) => {
  if (firmId === DEMO_FIRM_ID) {
    callback([
      {
        id: "m1",
        senderId: "demo-client-1",
        content: "Identity verified. Proceeding with sync.",
        timestamp: Timestamp.now(),
        readBy: [],
      } as any,
    ]);
    return () => {};
  }

  return onSnapshot(
    query(collection(db, "firms", firmId, "cases", caseId, "messages"), orderBy("timestamp", "asc")),
    (snapshot) => {
      callback(snapshot.docs.map((d) => ({ ...(d.data() as any), id: d.id } as TaxMessage)));
    }
  );
};

export const sendMessage = async (firmId: string, caseId: string, senderId: string, content: string) => {
  if (firmId === DEMO_FIRM_ID) return;

  const canonicalFirmId = await resolveFirmDocId(firmId);

  await addDoc(collection(db, "firms", canonicalFirmId, "cases", caseId, "messages"), {
    senderId,
    content,
    timestamp: serverTimestamp(),
    readBy: [senderId],
  });
};

export const subscribeToTasks = (
  firmId: string,
  caseId: string,
  callback: (tasks: TaxTask[]) => void
) => {
  if (firmId === DEMO_FIRM_ID) {
    callback([
      {
        id: "t1",
        title: "Upload 2024 W-2s",
        description: "Required.",
        status: "PENDING",
        type: "UPLOAD",
        createdAt: Timestamp.now(),
      } as any,
    ]);
    return () => {};
  }

  return onSnapshot(
    query(collection(db, "firms", firmId, "cases", caseId, "tasks"), orderBy("createdAt", "desc")),
    (snapshot) => {
      callback(snapshot.docs.map((d) => ({ ...(d.data() as any), id: d.id } as TaxTask)));
    }
  );
};

export const createTask = async (firmId: string, caseId: string, task: Omit<TaxTask, "id" | "createdAt">) => {
  if (firmId === DEMO_FIRM_ID) return;

  const canonicalFirmId = await resolveFirmDocId(firmId);

  await addDoc(collection(db, "firms", canonicalFirmId, "cases", caseId, "tasks"), {
    ...(task as any),
    createdAt: serverTimestamp(),
  });
};

export const updateTaskStatus = async (firmId: string, caseId: string, taskId: string, status: any) => {
  if (firmId === DEMO_FIRM_ID) return;

  const canonicalFirmId = await resolveFirmDocId(firmId);

  await updateDoc(doc(db, "firms", canonicalFirmId, "cases", caseId, "tasks", taskId), {
    status,
    completedAt: serverTimestamp(),
  });
};

export const deleteTask = async (firmId: string, caseId: string, taskId: string) => {
  if (firmId === DEMO_FIRM_ID) return;

  const canonicalFirmId = await resolveFirmDocId(firmId);

  await deleteDoc(doc(db, "firms", canonicalFirmId, "cases", caseId, "tasks", taskId));
};

export const updateCaseStatus = async (firmId: string, caseId: string, status: string) => {
  if (firmId === DEMO_FIRM_ID) return;

  const canonicalFirmId = await resolveFirmDocId(firmId);

  await updateDoc(doc(db, "firms", canonicalFirmId, "cases", caseId), {
    status,
    lastUpdated: serverTimestamp(),
  });
};

export const updateDocumentStatus = async (firmId: string, caseId: string, docId: string, status: string) => {
  if (firmId === DEMO_FIRM_ID) return;

  const canonicalFirmId = await resolveFirmDocId(firmId);

  await updateDoc(doc(db, "firms", canonicalFirmId, "cases", caseId, "documents", docId), {
    status,
    updatedAt: serverTimestamp(),
  });
};

export const getDocumentDownloadUrl = async (path: string) => {
  return path ? await getDownloadURL(ref(storage, path)) : "https://via.placeholder.com/800";
};

/**
 * ✅ REQUIRED BY SignaturePortal.tsx
 */
export const updateDocumentSignatureFields = async (
  firmId: string,
  caseId: string,
  docId: string,
  fields: any
) => {
  if (firmId === DEMO_FIRM_ID) return;

  const canonicalFirmId = await resolveFirmDocId(firmId);

  await updateDoc(doc(db, "firms", canonicalFirmId, "cases", caseId, "documents", docId), {
    signatureFields: fields,
    status: "ISSUE",
    updatedAt: serverTimestamp(),
  });
};

export const signDocumentField = async (
  firmId: string,
  caseId: string,
  docId: string,
  fieldId: string,
  ip: string
) => {
  if (firmId === DEMO_FIRM_ID) return;

  const canonicalFirmId = await resolveFirmDocId(firmId);

  const dRef = doc(db, "firms", canonicalFirmId, "cases", caseId, "documents", docId);
  const snap = await getDoc(dRef);
  if (!snap.exists()) return;

  const d = snap.data() as any;
  const currentFields = Array.isArray(d.signatureFields) ? d.signatureFields : [];

  const nextFields = currentFields.map((f: any) =>
    f.id === fieldId
      ? { ...f, signed: true, signedAt: Timestamp.now(), ipAddress: ip }
      : f
  );

  await updateDoc(dRef, { signatureFields: nextFields, updatedAt: serverTimestamp() });

  if (nextFields.length > 0 && nextFields.every((f: any) => f.signed)) {
    await updateDoc(dRef, { status: "APPROVED", updatedAt: serverTimestamp() });
  }
};

export const subscribeToFirmUpdates = (firmId: string, callback: (u: FirmUpdate[]) => void) => {
  if (firmId === DEMO_FIRM_ID) {
    callback([
      {
        id: "u1",
        title: "Simulation Active",
        content: "Environment running in sandbox mode.",
        authorName: "Admin",
        createdAt: Timestamp.now(),
      } as any,
    ]);
    return () => {};
  }

  return onSnapshot(
    query(collection(db, "firms", firmId, "updates"), orderBy("createdAt", "desc")),
    (snap) => callback(snap.docs.map((d) => ({ ...(d.data() as any), id: d.id } as FirmUpdate)))
  );
};

export const addFirmUpdate = async (firmId: string, update: Omit<FirmUpdate, "id" | "createdAt">) => {
  if (firmId === DEMO_FIRM_ID) return;

  const canonicalFirmId = await resolveFirmDocId(firmId);

  await addDoc(collection(db, "firms", canonicalFirmId, "updates"), { ...(update as any), createdAt: serverTimestamp() });
};

export const deleteFirmUpdate = async (firmId: string, updateId: string) => {
  if (firmId === DEMO_FIRM_ID) return;

  const canonicalFirmId = await resolveFirmDocId(firmId);

  await deleteDoc(doc(db, "firms", canonicalFirmId, "updates", updateId));
};

/**
 * Cloud Functions (callables)
 */
export const crmOAuthStart = httpsCallable<any, any>(functions, "ghlOAuthStart");
export const crmSyncUsersNow = httpsCallable<any, any>(functions, "ghlSyncUsersNow");

export const crmGetStatus = async (args: { locationId: string }) => {
  if (args.locationId === DEMO_FIRM_ID) return { connected: true, locationId: "DEMO-CRM-LOC" };
  return (await httpsCallable<any, any>(functions, "ghlGetStatus")(args)).data;
};

export const ghlOAuthStart = crmOAuthStart;
export const ghlSyncUsersNow = crmSyncUsersNow;
export const ghlGetStatus = crmGetStatus;

/**
 * Role assignment helper used by FirmSettings page
 */
export const firmAssignUserRole = async (args: any) => {
  if (!args?.locationId || args.locationId === DEMO_FIRM_ID) return;

  const canonicalFirmId = await resolveFirmDocId(args.locationId);

  const ghlUserId = args.crmUserId || args.ghlUserId;
  if (!ghlUserId) return;

  await setDoc(
    doc(db, "firms", canonicalFirmId, "users", ghlUserId),
    { filershubRole: args.filershubRole, updatedAt: serverTimestamp() },
    { merge: true }
  );
};

/**
 * Staff roster subscription used by FirmSettings page
 */
export const subscribeToSyncedStaff = (lid: string, cb: (u: SyncedStaffUser[]) => void) => {
  if (lid === DEMO_FIRM_ID) {
    cb([
      {
        crmUserId: "s1",
        ghlUserId: "s1",
        email: "hannah@hannahfinancial.com",
        name: "Hannah Owner",
        crmRole: "admin",
        ghlRole: "admin",
        filershubRole: "FirmOwner",
        active: true,
      } as any,
    ]);
    return () => {};
  }

  // NOTE: subscription cannot be async; lid is expected to already be canonical
  return onSnapshot(collection(db, "firms", lid, "users"), (snap) =>
    cb(
      snap.docs.map((d) => {
        const data = d.data() as any;
        return {
          crmUserId: d.id,
          ghlUserId: d.id,
          ...data,
          crmRole: data.crmRole || data.ghlRole || "user",
          ghlRole: data.ghlRole || data.crmRole || "user",
        } as SyncedStaffUser;
      })
    )
  );
};

/**
 * Invitations
 */
export const inviteEmployee = async (firmId: string, email: string, fullName: string, role: string) => {
  if (firmId === DEMO_FIRM_ID) return true;

  const canonicalFirmId = await resolveFirmDocId(firmId);

  await addDoc(collection(db, "firms", canonicalFirmId, "invitations"), {
    email,
    fullName,
    role,
    invitedAt: serverTimestamp(),
    status: "PENDING",
  });

  return true;
};

/**
 * Demo data
 */
export const MOCK_DEMO_CASES: TaxCase[] = [
  {
    id: "demo-case-001",
    clientId: "demo-client-1",
    clientName: "John Thompson",
    year: 2024,
    status: TaxStatus.INFO_RECEIVED,
    filingType: "1040 Individual",
    lastUpdated: Timestamp.now(),
    assignedStaffId: "dev_TaxPro",
    assignedStaffName: "Hannah Pro",
  } as any,
];

const MOCK_DOCS: TaxDocument[] = [
  {
    id: "d1",
    fileName: "W2_Amazon.pdf",
    category: "Income",
    status: "APPROVED",
    uploadedAt: new Date().toISOString(),
    mimeType: "application/pdf",
    sizeBytes: 450000,
    storagePath: "",
    caseId: "demo-case-001",
    clientId: "demo-client-1",
    clientName: "John Thompson",
    year: 2024,
    filingType: "1040",
    firmId: DEMO_FIRM_ID,
    uploadedBy: "demo-client-1",
  } as any,
];
